import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs';
import { RoleAccessService } from '../../shared/services/role-access.service';
import { SocketService } from './socket.service';
import { RoutePath } from '../../shared/enums/common.enum';
import { ToastrService } from '../../shared/services/toastr.service';
import { LocalStorage } from '../../shared/services/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class SubAdminRoleSyncService {
  private readonly router = inject(Router);
  private readonly socketService = inject(SocketService);
  private readonly roleAccessService = inject(RoleAccessService);
  private readonly toastrService = inject(ToastrService);
  private readonly localStorageService = inject(LocalStorage);
  private readonly destroyRef = inject(DestroyRef);

  private isInitialized = false;
  private isRolesListenerBound = false;
  private lastForcedRedirectAt = 0;
  private readonly roleRouteMatchers = this.buildRoleRouteMatchers();

  init(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.roleAccessService.getCurrentSubAdminId()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(subAdminId => {
        if (subAdminId) {
          this.bindRoleUpdatesOnce();
          this.socketService.connectForSubAdmin();
          return;
        }

        this.socketService.disconnect();
      });
  }

  /**
   * Bind the sub-admin role updates event from the socket service once.
   * If the received event has an 'inactive' status, force the user to log out.
   * If the event is successfully applied to the current sub-admin, redirect
   * to the current route if the current route is forbidden after the event is applied.
   */
  private bindRoleUpdatesOnce(): void {
    if (this.isRolesListenerBound) return;
    this.isRolesListenerBound = true;

    this.socketService.on('sub_admin_roles_updated')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(payload => {
        if (this.getEventStatus(payload) === 'inactive') {
          this.forceLogout();
          return;
        }

        const isApplied = this.roleAccessService.applyRolesUpdatedEvent(payload);
        if (isApplied) {
          this.redirectIfCurrentRouteForbidden();
        }
      });
  }

/**
 * Returns the status of the sub-admin roles updated event.
 * The status can be either 'active' or 'inactive'.
 * If the status is 'inactive', the user will be forced to log out.
 * @param payload - The roles updated event payload.
 * @returns The status of the event.
 */
  private getEventStatus(payload: { status?: string; data?: { user?: Record<string, unknown> } }): string {
    const nestedStatus = payload.data?.user?.['status'];
    const status = typeof payload.status === 'string' ? payload.status : nestedStatus;
    return typeof status === 'string' ? status.toLowerCase() : '';
  }

  /**
   * Forces the user to log out by showing a warning toast, disconnecting from the socket,
   * clearing all local storage, and redirecting to the dashboard.
   */
  private forceLogout(): void {
    this.toastrService.showToast(
      'Your account is inactive. Please contact Admin.',
      'warning'
    );
    this.socketService.disconnect();
    this.localStorageService.clearAllStorage();
    void this.router.navigate(['']);
  }

  /**
   * Redirects the user to the dashboard if the current route is forbidden
   * (i.e. the user does not have the required role for the current route).
   * If the user has no active roles, it will also redirect to the dashboard.
   * @private
   */
  private redirectIfCurrentRouteForbidden(): void {
    const currentPath = this.normalizePath(this.router.url || '');
    const dashboardPath = `/${RoutePath.DASHBOARD}`;
    if (!currentPath || currentPath.startsWith(dashboardPath)) {
      return;
    }

    const activeRoles = new Set(this.roleAccessService.getActiveRoleIds());
    if (activeRoles.size === 0) {
      this.navigateDashboardWithPermissionToast(dashboardPath);
      return;
    }

    const requiredRoleId = this.resolveRequiredRoleId(currentPath);
    if (requiredRoleId && !activeRoles.has(requiredRoleId)) {
      this.navigateDashboardWithPermissionToast(dashboardPath);
    }
  }

  /**
   * Redirects the user to the dashboard and shows a warning toast if the
   * user's access permissions were updated in the last 1.5 seconds.
   * @param dashboardPath The path to the dashboard route.
   */
  private navigateDashboardWithPermissionToast(dashboardPath: string): void {
    const now = Date.now();
    if (now - this.lastForcedRedirectAt > 1500) {
      this.toastrService.showToast(
        'Your access permissions were updated. Redirected to Dashboard.',
        'warning'
      );
      this.lastForcedRedirectAt = now;
    }

    void this.router.navigate([dashboardPath]);
  }

/**
 * Resolves the required role identifier from a given path.
 * It checks the given path for matching route prefixes from the roleRouteMatchers array.
 * If a match is found, it returns the roleId associated with the matching route prefix.
 * If no match is found, it returns null.
 * @param path The path to resolve the required role identifier from
 * @returns The required role identifier or null if no match is found
 */
  private resolveRequiredRoleId(path: string): string | null {
    const match = this.roleRouteMatchers.find(route => path.startsWith(route.prefix));
    return match?.roleId || null;
  }

  /**
   * Normalizes a given URL path by removing any query string and fragment identifier, and
   * trimming any trailing slashes.
   * @param url The URL path to normalize
   * @returns The normalized URL path
   */
  private normalizePath(url: string): string {
    const withoutQuery = url.split('?')[0].split('#')[0];
    return withoutQuery.length > 1 && withoutQuery.endsWith('/')
      ? withoutQuery.slice(0, -1)
      : withoutQuery;
  }

/**
 * Builds an array of route matchers for roles.
 * Each matcher is an object with a prefix (the route prefix) and a roleId (the id of the role).
 * The matchers are sorted in descending order of prefix length, so longer prefixes are matched first.
 * @returns An array of route matchers for roles
 */
  private buildRoleRouteMatchers(): Array<{ prefix: string; roleId: string }> {
    return [
      { prefix: `/${RoutePath.CATEGORY}/${RoutePath.FAQ_CATEGORY}`, roleId: 'faq' },
      { prefix: `/${RoutePath.CATEGORY}/${RoutePath.BLOG_CATEGORY}`, roleId: 'blog' },
      { prefix: `/${RoutePath.CATEGORY}/${RoutePath.PARTNER_CATEGORY}`, roleId: 'partners' },
      { prefix: `/${RoutePath.CATEGORY}/${RoutePath.AUDIT_CATEGORY}`, roleId: 'audit-report' },
      { prefix: `/${RoutePath.FAQ}`, roleId: 'faq' },
      { prefix: `/${RoutePath.BLOG}`, roleId: 'blog' },
      { prefix: `/${RoutePath.PARTNERS}`, roleId: 'partners' },
      { prefix: `/${RoutePath.AUDIT_REPORT}`, roleId: 'audit-report' },
      { prefix: `/${RoutePath.ANNOUNCEMENT}`, roleId: 'announcements' },
      { prefix: `/${RoutePath.VOTE_DOMAIN}`, roleId: 'vote-domain' },
      { prefix: '/banners', roleId: 'banners' }
    ].sort((a, b) => b.prefix.length - a.prefix.length);
  }
}
